import { Alert } from '@creditornot/cb-alert';
import { Button } from '@creditornot/cb-button';
import { typographyCss } from '@creditornot/cb-ingredients';
import { Spinner } from '@creditornot/cb-progress';
import { Modal, ModalContent, ModalFooter } from '@creditornot/cbt-modal';
import React, { useCallback } from 'react';
import toast from 'react-hot-toast';
import { FormattedMessage, useIntl } from 'react-intl';
import styled from 'styled-components';

import { usePluralization } from 'common/hooks';
import useCurrentUser from 'common/hooks/useCurrentUser';
import { listingManagerInteractionClickConnectCatalog } from 'core/telemetry/avo/Avo';
import { useSelectionData } from 'modules/shared-menu/hooks/useSelectionData';
import { useSelectionProducts } from 'modules/shared-menu/pages/SharedMenuEditorPage/hooks/useSelectionProducts';
import { OfferWithSelectionAndAuthor, OfferingApiError } from 'modules/shared-menu/types';

import useBulkProductConnect from './useBulkProductConnect';
import BulkActionErrorList from '../BulkActionErrorList';
import { buildUpdateOffersPayload } from '../transformers';
import { useBatchUpdateOffers } from '../useBatchUpdateOffers';
import { updateLocalSelectionData } from '../utils';

type Props = {
  onClose: () => void;
  selectedIds: Set<string>;
};

const LoadingState = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 472px;
`;

const SubHeader = styled.div`
  ${typographyCss.Body3()};
  font-weight: 700;
  margin-block-start: 16px;
  margin-block-end: 4px;
`;

const StyledAlert = styled(Alert)`
  margin-block-start: 16px;
`;

const WarningMessage = styled.div`
  ${typographyCss.Body3()};
  font-weight: 700;
`;

const StyledFooter = styled(ModalFooter)`
  display: flex;
  justify-content: flex-end;
`;

const CancelButton = styled(Button).attrs({ variant: 'secondary', size: 'small' })`
  margin-inline-end: 16px;
`;

const BulkProductConnectModal = ({ selectedIds, onClose }: Props) => {
  const intl = useIntl();
  const { offers, updateSelectionData, selection } = useSelectionData();
  const { formatPluralMessage } = usePluralization();
  const { data: user } = useCurrentUser();
  const { updateSelectionProductsCache } = useSelectionProducts();
  const { executeBatchUpdateOffers, isLoading: isUpdating } = useBatchUpdateOffers();
  const { isLoadingProducts, noGtins, alreadyConnected, matchingGtinOffers, noMatchingGtin, foundProducts } =
    useBulkProductConnect({ selectedIds, offers });

  const onSuccess = useCallback(
    (successfullyUpdatedOffers: OfferWithSelectionAndAuthor[]) => {
      toast.success(
        formatPluralMessage({
          id: 'menu.bulkActions.connectProductToCatalog.successToastMessage',
          count: successfullyUpdatedOffers.length,
        }),
      );
      updateLocalSelectionData(successfullyUpdatedOffers, updateSelectionData);
    },
    [formatPluralMessage, updateSelectionData],
  );

  const onError = useCallback(
    (errors: OfferingApiError[]) => {
      toast.error(
        <BulkActionErrorList
          allOffers={offers}
          errors={errors}
          selectionPrimaryLanguage={selection.primary_language}
          description={intl.formatMessage({ id: 'shared-menu.bulk-actions.toasts.update.error-description' })}
        />,
        { duration: Infinity },
      );
    },
    [intl, offers, selection.primary_language],
  );

  const onConnectProducts = useCallback(async () => {
    updateSelectionProductsCache(...foundProducts);
    const offerPayloads = buildUpdateOffersPayload(matchingGtinOffers, selection.id, user?._id.$oid ?? '');
    await executeBatchUpdateOffers({ offerPayloads, onSuccess, onError });
    listingManagerInteractionClickConnectCatalog({
      selectionId: selection.id,
      selectedItems: matchingGtinOffers.map(offer => offer.id),
    });
    onClose();
  }, [
    executeBatchUpdateOffers,
    foundProducts,
    matchingGtinOffers,
    onClose,
    onError,
    onSuccess,
    selection.id,
    updateSelectionProductsCache,
    user?._id.$oid,
  ]);

  return (
    <Modal
      title={intl.formatMessage({ id: 'menu.bulkActions.connectProductToCatalog.title' })}
      sizePreset={'centered:large'}
      onDismiss={onClose}
      open
      disableContentPadding
    >
      {isLoadingProducts ? (
        <LoadingState>
          <Spinner size="large" />
        </LoadingState>
      ) : (
        <>
          <ModalContent>
            <FormattedMessage
              id="menu.bulkActions.connectProductToCatalog.description"
              values={{ strong: chunks => <strong>{chunks}</strong>, count: matchingGtinOffers.length }}
            />
            <SubHeader>
              {intl.formatMessage({ id: 'menu.bulkActions.connectProductToCatalog.matchingGtinSubheader' })}
            </SubHeader>
            {formatPluralMessage({
              id: 'menu.bulkActions.connectProductToCatalog.matchingGtinDescription',
              count: matchingGtinOffers.length,
            })}
            <SubHeader>
              {intl.formatMessage({ id: 'menu.bulkActions.connectProductToCatalog.noMatchingGtinSubheader' })}
            </SubHeader>
            {formatPluralMessage({
              id: 'menu.bulkActions.connectProductToCatalog.noMatchingGtinDescription',
              count: noMatchingGtin,
            })}
            <SubHeader>
              {intl.formatMessage({ id: 'menu.bulkActions.connectProductToCatalog.noGtinsSubheader' })}
            </SubHeader>
            {formatPluralMessage({
              id: 'menu.bulkActions.connectProductToCatalog.noGtinsDescription',
              count: noGtins,
            })}
            <SubHeader>
              {intl.formatMessage({ id: 'menu.bulkActions.connectProductToCatalog.alreadyConnectedSubheader' })}
            </SubHeader>
            {formatPluralMessage({
              id: 'menu.bulkActions.connectProductToCatalog.alreadyConnectedDescription',
              count: alreadyConnected,
            })}
            <StyledAlert variant="warning">
              <WarningMessage>
                {formatPluralMessage({
                  id: 'menu.bulkActions.connectProductToCatalog.overwritingWarningMessage',
                  count: matchingGtinOffers.length,
                })}
              </WarningMessage>
            </StyledAlert>
          </ModalContent>
          <StyledFooter>
            <CancelButton onClick={onClose}>{intl.formatMessage({ id: 'state.cancel' })}</CancelButton>
            <Button
              variant="primary"
              size="small"
              onClick={onConnectProducts}
              loading={isLoadingProducts || isUpdating}
              disabled={matchingGtinOffers.length < 1}
            >
              {formatPluralMessage({
                id: 'menu.bulkActions.connectProductToCatalog.connectButton',
                count: matchingGtinOffers.length,
              })}
            </Button>
          </StyledFooter>
        </>
      )}
    </Modal>
  );
};

export default BulkProductConnectModal;
